import {computed, Directive, HostListener, OnInit, Optional, Renderer2, WritableSignal, input} from '@angular/core';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatInput} from '@angular/material/input';
import {TranslateService} from '@ngx-translate/core';

import {SignalBasedBinderInput, SignalBinderModel, SignalValidationStruct} from '../../types/signal-model.type';

/**
 * @example
 * <input matInput [SignalBinder]="[loginForm, 'company', [{type: 'required', errorMessage: 'pages.login.errors.unrecognizedtenant'}]]"/>
 **/
@Directive({
	selector: '[matInput][SignalBinder], mat-checkbox[SignalBinder]',
	standalone: true,
})
export class SignalBinderDirective<T extends object> implements OnInit {
	/**
	 * @type {SignalBasedBinderInput}
	 * @memberof SignalBinderDirective
	 * @description [model: WritableSignal<T>, prop: keyof T] (T extends object) is a generic type
	 */
	public readonly SignalBinder = input.required<SignalBasedBinderInput<T>>();

	get model(): WritableSignal<T> | SignalBinderModel<T> {
		return this.SignalBinder()[0];
	}

	private prop = computed(() => this.SignalBinder()[1]);
	private validations = computed(() => this.SignalBinder()[2]);

	private _needValidation = false;
	private _isSignalBinderClass = false;

	constructor(
		@Optional() private matInput: MatInput,
		@Optional() private matCheckbox: MatCheckbox,
		private translate: TranslateService,
		private renderer: Renderer2,
	) {}

	/**
	 * @description get the target type (mat-input, mat-checkbox, etc.)
	 */
	private get targetType(): typeof MatInput | typeof MatCheckbox | null {
		if (this.matInput) return MatInput;
		if (this.matCheckbox) return MatCheckbox;
		return null;
	}

	private get signalBinder$(): WritableSignal<T> {
		return this._isSignalBinderClass ? (this.SignalBinder()[0] as SignalBinderModel<T>).signal$ : (this.SignalBinder()[0] as WritableSignal<T>);
	}

	ngOnInit(): void {
		const [, prop, validations] = this.SignalBinder();
		const target = this.targetType;
		this._needValidation = validations?.length > 0;

		if (this.model instanceof SignalBinderModel) {
			this._isSignalBinderClass = true;
			if (this._needValidation) this.model.setPropValidity(this.prop());
			this.model.signal$.update(m => {
				m[prop] = this.signalBinder$()[prop];
				return m;
			});
		}
		const value = this.signalBinder$()[prop];

		switch (target) {
			case MatInput:
				this.matInput.value = value || '';
				break;
			case MatCheckbox:
				this.matCheckbox.checked = Boolean(value);
				break;
			default:
				throw new Error('Invalid target type');
		}

		this.updateModel(value);
	}

	/**
	 * @description listen to change event of the target element (mat-input, mat-checkbox, etc.)
	 * @param value
	 */
	@HostListener('input', ['$event.target.value'])
	onInput(value: ObjectTypes.TypeValue<T>): void {
		if (this.matInput) this.updateModel(value);
	}

	@HostListener('change', ['$event.checked'])
	onChange(value: ObjectTypes.TypeValue<T>): void {
		if (this.matCheckbox) this.updateModel(value);
	}

	/**
	 * @description update the signal-based model with the value of the target element (mat-input, mat-checkbox, etc.)
	 * @param value
	 * @private
	 */
	private updateModel(value: ObjectTypes.TypeValue<T>): void {
		const [, prop] = this.SignalBinder();
		this.signalBinder$.update(m => {
			return {...m, [prop]: value};
		});
		if (this._needValidation) this.validate(value);
	}

	/**
	 * @description validate the value of the target element (mat-input, mat-checkbox, etc.)
	 * @param _value {ObjectTypes.TypeValue} - the value to validate
	 * @private
	 */
	private validate(_value: ObjectTypes.TypeValue<T>): void {
		const validations = this.validations();
		if (!validations || typeof _value !== 'string') return;
		const value: string = _value;

		const failedValidations: SignalValidationStruct[] = [];
		for (const _validation of validations) {
			const validation = typeof _validation === 'string' ? {type: _validation, value: undefined} : _validation;
			switch (validation.type) {
				case 'required':
					!value ? failedValidations.push(validation) : null;
					break;
				case 'minLength':
					value.length < validation.value ? failedValidations.push(validation) : null;
					break;
				case 'maxLength':
					value.length > validation.value ? failedValidations.push(validation) : null;
					break;
				case 'pattern':
					!new RegExp(validation.value).test(value) ? failedValidations.push(validation) : null;
					break;
				case 'email':
					!new RegExp('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,4}$').test(value) ? failedValidations.push(validation) : null;
					break;
			}
		}
		this.setValidity(failedValidations);
	}

	/**
	 * @description set validity of the target element (mat-input, mat-checkbox, etc.)
	 * @param failedValidations
	 * @private
	 */
	private setValidity(failedValidations: SignalValidationStruct[]): void {
		const target = this.targetType;
		const isValid = failedValidations.length === 0;
		if (this._isSignalBinderClass) {
			const signalBinder = this.SignalBinder()[0] as SignalBinderModel<T>;
			signalBinder.setPropValidity(this.prop(), isValid);
		}
		switch (target) {
			case MatInput:
				this.matInput.errorState = !isValid;
				if (!isValid) {
					const errorMessage: string = this.generateErrorMessage(failedValidations);
					this.updateMatError(this.matInput, errorMessage);
				} else {
					this.updateMatError(this.matInput);
				}
				break;
		}
	}

	/**
	 * @description generate error message from failed services
	 * @param validations {SignalValidationStruct[]} - failed services to generate error message from
	 * @private
	 */
	private generateErrorMessage(validations: SignalValidationStruct[]): string {
		const errorMessages: string[] = validations.flatMap(validation => {
			return validation?.errorMessage ? [this.translate.instant(validation.errorMessage)] : [];
		});
		return errorMessages.join(', ');
	}

	/**
	 * @description update mat-error element
	 * @param target {MatInput} - the target mat-input
	 * @param errorMessage {string} optional - if not provided, the error message will be cleared
	 * @private
	 */
	private updateMatError(target: MatInput, errorMessage?: string): void {
		setTimeout(() => {
			const matErrorElement = target['_formField']['_elementRef']?.nativeElement?.querySelector('mat-error');
			if (matErrorElement) {
				this.renderer.setProperty(matErrorElement, 'innerHTML', errorMessage || '*');
			}
		});
	}
}
